import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Download,
  FileText,
  Mail,
  Server,
  ShieldAlert,
  Upload,
  X,
} from 'lucide-react';
import {
  getFileDownloadUrl,
  listProjectFiles,
  type PrismeUploadedFile,
  uploadProjectFiles,
} from '../services/prismeFilesService';

interface OricomAcquisitionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'resume' | 'finance' | 'nogo' | 'files' | 'email' | 'avis-ca' | 'criteres';

const PROJECT_SLUG = 'acquisition-oricom-colocation-serko-nolin';

const tabs: { id: TabId; label: string; icon: typeof FileText }[] = [
  { id: 'resume', label: 'Résumé', icon: FileText },
  { id: 'finance', label: 'Chiffres', icon: Server },
  { id: 'nogo', label: 'NO GO', icon: ShieldAlert },
  { id: 'files', label: 'Fichiers', icon: Upload },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'avis-ca', label: 'Avis CA', icon: Mail },
  { id: 'criteres', label: 'Critères', icon: CheckCircle2 },
];

const sellerFinancials = [
  { year: '2023', revenue: '566 k$', ebitda: '266 k$', margin: '47 %' },
  { year: '2024', revenue: '634 k$', ebitda: '293 k$', margin: '46 %' },
  { year: '2025', revenue: '664 k$', ebitda: '303 k$', margin: '46 %' },
  { year: '2026 proj.', revenue: '662 k$', ebitda: '293 k$', margin: '44 %' },
  { year: '2030 proj.', revenue: '646 k$', ebitda: '264 k$', margin: '41 %' },
];

const noGoReasons = [
  {
    title: "Capacité d'équipe Cloud",
    text: "L'équipe Cloud de Richard est déjà à pleine charge. Deux salles avec SLA exigeraient une embauche et une astreinte coûteuses, avec risque direct sur les opérations existantes.",
  },
  {
    title: 'Hors core business',
    text: "La colocation est un métier d'infrastructure capitalistique, peu scalable et peu synergique avec le modèle Cloud et services gérés de BZ.",
  },
  {
    title: 'Économie non viable',
    text: "Même à 0 $ d'acquisition, les obligations transférées, la main-d'oeuvre, le transit, les baux et l'entretien annulent la rentabilité.",
  },
  {
    title: 'Risque Serko',
    text: "Serko porte environ 65 % de l'EBITDA, mais le bail expire le 31 mai 2027, avec indexation et consentement du bailleur requis.",
  },
  {
    title: 'Mur de renouvellements',
    text: "Environ 88 % du MRR arrive à renouvellement entre juillet 2026 et juin 2027, au pire moment d'une transition.",
  },
];

const reusableCriteria = [
  'Normaliser avant de valoriser : remplacer les allocations vendeurs par les vrais coûts BZ.',
  "Le prix d'achat n'est pas le coût : mesurer les obligations transférées avant le multiple.",
  "Tester la capacité d'équipe avant le modèle financier.",
  "Tester le fit : même dans des conditions parfaites, voudrait-on opérer ceci 5 ans?",
  'Au-delà de 50 % de MRR renouvelable pendant la transition : earnout/retenue majeure ou passer.',
  'Si le vendeur devient fournisseur critique après clôture, encadrer fortement ou passer.',
  'Quand financier normalisé, stratégie et instinct dirigeant disent non : ne pas sur-analyser.',
];

const withdrawalEmail = `Objet : Suivi - dossier colocation Oricom

Bonjour Alain, bonjour Mikael,

Merci encore pour votre transparence et pour le temps consacré aux échanges autour du service de colocation Oricom.

Après analyse interne, nous avons décidé de ne pas poursuivre le dossier et de ne pas déposer de LOI.

La décision n'est pas liée à la qualité des échanges ni à l'intérêt du dossier en soi. Elle tient surtout à notre lecture opérationnelle : exploiter ce service de façon autonome exigerait une structure de coûts, une disponibilité d'équipe, des engagements d'exploitation et des responsabilités qui ne cadrent pas suffisamment avec nos priorités actuelles en services Cloud et services gérés.

Dans ce contexte, nous préférons fermer le dossier proprement plutôt que de vous faire avancer dans une démarche qui ne serait pas alignée avec notre capacité réelle d'exécution.

J'ai beaucoup apprécié la qualité des discussions et la transparence de votre approche. Nous demeurons ouverts à échanger dans le futur si une opportunité distincte, davantage alignée avec notre coeur d'affaires en services Cloud ou services gérés, se présente.

Merci encore,

Dany`;

const boardNoticeEmail = `Objet : Suivi transparent - decision NO-GO sur le dossier Oricom / colocation

Envoi officiel documenté
Date : 13 juillet 2026
Destinataires : François Delisle <francois.delisle@bzinc.ca>, Richard Delisle <richard.delisle@bzinc.ca>
CC : Dany Bourassa <dany.bourassa@bouinvest.com>
Gmail message ID : 19f5c33d4a65cfaf
Format : HTML UX + fallback texte

Bonjour François,
Bonjour Richard,

Je vous écris pour vous tenir informés de façon transparente de la conclusion du dossier Oricom / Bell concernant la reprise potentielle du service de colocation, principalement autour des sites Serko et Nolin.

Après analyse interne, j’ai décidé que BZ ne poursuivra pas le dossier. Nous ne déposerons pas de LOI, nous ne ferons pas de contre-proposition, et nous ne gardons pas de porte ouverte active sur ce dossier précis, que ce soit pour le service de colocation ou pour l’immeuble Nolin.

La décision ne découle pas d’un manque d’intérêt initial ni de la qualité des échanges avec Oricom/Bell. Au contraire, les échanges ont été professionnels et transparents. La décision vient plutôt de notre lecture opérationnelle et stratégique: ce dossier ne cadre pas suffisamment avec la capacité réelle de BZ ni avec nos priorités actuelles en services Cloud et services gérés.

Les principaux facteurs de décision sont les suivants:

1. Capacité opérationnelle Cloud
L’équipe Cloud de Richard est déjà fortement sollicitée. Reprendre deux salles de colocation avec des attentes de disponibilité, de surveillance, d’astreinte, d’infrastructure, de réseau, d’énergie, de CVC et de continuité de service créerait une pression importante sur l’équipe et pourrait augmenter le risque sur nos opérations existantes.

2. Fit stratégique limité
La colocation est un métier d’infrastructure plus capitalistique, moins scalable et moins directement synergique avec notre modèle de services gérés et Cloud. Même si certains éléments peuvent sembler connexes à nos activités, l’exploitation autonome d’un tel service demanderait une structure différente de celle que nous voulons prioriser.

3. Rentabilité non transposable
Les chiffres vendeur ne sont pas directement transposables à BZ. Même avec un prix d’acquisition très bas, voire nul, les obligations transférées, la main-d’oeuvre réaliste, les coûts de transit/réseau, les baux, l’entretien, l’énergie, la surveillance et les responsabilités d’exploitation peuvent annuler ou dépasser la rentabilité apparente.

4. Risque spécifique du site Serko
Serko représente une part importante de l’EBITDA du dossier, mais le site est loué et le bail arrive à échéance le 31 mai 2027. Le renouvellement, l’indexation et le consentement du bailleur ajoutent un risque important au moment même où BZ devrait stabiliser une transition.

5. Mur de renouvellements clients
Une part très importante du revenu mensuel arrive à renouvellement entre juillet 2026 et juin 2027. Cela concentre trop de risque commercial et opérationnel dans la première année de transition.

6. Aucun plan B / plan C suffisamment robuste
Si les principaux risques se matérialisent — perte de clients, coûts plus élevés que prévu, bail non renouvelé, besoin d’équipe supplémentaire, incidents d’exploitation — le dossier pourrait rapidement devenir défavorable pour BZ.

Ma conclusion est donc qu’il est préférable de fermer le dossier proprement maintenant, plutôt que d’engager BZ dans une démarche qui pourrait sembler intéressante sur papier mais qui ne respecte pas assez notre réalité opérationnelle, notre capacité d’exécution et nos priorités stratégiques.

J’ai tout de même apprécié la démarche, et la relation avec Oricom/Bell doit rester professionnelle. Si une opportunité distincte, plus alignée avec notre coeur d’affaires en services Cloud ou services gérés, se présente éventuellement, elle pourra être analysée séparément. Mais pour le dossier actuel de reprise de colocation Serko/Nolin, la décision est NO-GO.

Je voulais que vous ayez l’information complète, par transparence, puisque le dossier touche une décision stratégique potentielle pour BZ.

Merci,

Dany`;

function formatBytes(size: number) {
  if (!Number.isFinite(size) || size <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
  return `${(size / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

function MetricCard({ label, value, note, tone = 'slate' }: { label: string; value: string; note: string; tone?: 'slate' | 'red' | 'amber' | 'emerald' }) {
  const tones = {
    slate: 'border-slate-200 bg-white text-slate-900',
    red: 'border-red-200 bg-red-50 text-red-950',
    amber: 'border-amber-200 bg-amber-50 text-amber-950',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-950',
  };
  return (
    <div className={`rounded-lg border p-4 shadow-sm ${tones[tone]}`}>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-2 text-2xl font-bold">{value}</p>
      <p className="mt-1 text-sm text-slate-600">{note}</p>
    </div>
  );
}

export default function OricomAcquisitionModal({ isOpen, onClose }: OricomAcquisitionModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('resume');
  const [files, setFiles] = useState<PrismeUploadedFile[]>([]);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [isFilesLoading, setIsFilesLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const totalFileSize = useMemo(() => files.reduce((sum, file) => sum + file.sizeBytes, 0), [files]);

  useEffect(() => {
    if (!isOpen) return;
    setActiveTab('resume');
    setIsFilesLoading(true);
    setFilesError(null);
    listProjectFiles(PROJECT_SLUG)
      .then((response) => setFiles(response.files))
      .catch((error) => setFilesError(error.message || 'Impossible de charger les fichiers.'))
      .finally(() => setIsFilesLoading(false));
  }, [isOpen]);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles?.length) return;
    try {
      setIsUploading(true);
      setFilesError(null);
      await uploadProjectFiles(PROJECT_SLUG, selectedFiles);
      const response = await listProjectFiles(PROJECT_SLUG);
      setFiles(response.files);
    } catch (error) {
      setFilesError(error instanceof Error ? error.message : 'Upload impossible.');
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/75 p-4 backdrop-blur-sm"
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            className="flex max-h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-xl bg-slate-50 shadow-2xl"
          >
            <div className="border-b border-red-900/20 bg-gradient-to-r from-slate-950 via-slate-900 to-red-950 px-6 py-5 text-white">
              <div className="flex items-start justify-between gap-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-500/20 ring-1 ring-red-300/40">
                    <ShieldAlert className="h-6 w-6 text-red-100" />
                  </div>
                  <div>
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-red-500 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">Fermé - NO-GO</span>
                      <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold text-slate-100">Décision juillet 2026</span>
                      <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold text-slate-100">Source Fable 5 validée</span>
                    </div>
                    <h2 className="text-2xl font-bold tracking-tight">Acquisition Oricom Colocation (Serko + Nolin)</h2>
                    <p className="mt-1 max-w-4xl text-sm leading-6 text-slate-200">
                      Dossier stratégique BZ archivé : aucune LOI, aucune contre-proposition, aucune porte ouverte sur la colocation ni sur l'immeuble Nolin.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg p-2 text-slate-200 transition hover:bg-white/10 hover:text-white"
                  aria-label="Fermer"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="border-b border-slate-200 bg-white px-6">
              <div className="flex gap-2 overflow-x-auto py-3">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                        isActive ? 'bg-slate-900 text-white shadow' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'resume' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                    <MetricCard label="Décision" value="NO-GO" note="Dossier fermé sans LOI" tone="red" />
                    <MetricCard label="Revenus vendeur" value="662 k$" note="Projection 2026 consolidée" />
                    <MetricCard label="EBITDA vendeur" value="293 k$" note="Non transposable à BZ" tone="amber" />
                    <MetricCard label="MRR renouvelable" value="88 %" note="Juil. 2026 à juin 2027" tone="red" />
                  </div>

                  <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.2fr_0.8fr]">
                    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                      <h3 className="mb-3 text-lg font-bold text-slate-950">Énoncé exécutif</h3>
                      <p className="text-sm leading-7 text-slate-700">
                        BZ ne poursuit pas ce dossier. Même à un prix d'acquisition de 0 $, la reprise transfère des obligations opérationnelles
                        dont le coût réel dépasse ou annule la rentabilité du service, dans un métier hors des priorités Cloud et services gérés,
                        avec l'équipe Cloud de Richard déjà à pleine capacité et sans plan B ni plan C crédible si les risques principaux se matérialisent.
                      </p>
                    </section>
                    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                      <h3 className="mb-3 text-lg font-bold text-slate-950">Relation Bell / Oricom</h3>
                      <p className="text-sm leading-7 text-slate-700">
                        Contacts Alain et Mikael : relation de qualité à préserver. Suivi passif seulement. BZ reste ouvert à de futurs dossiers
                        distincts uniquement s'ils sont alignés au coeur Cloud ou services gérés.
                      </p>
                    </section>
                  </div>

                  <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <h3 className="mb-4 text-lg font-bold text-slate-950">Contexte du dossier</h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      {[
                        ['Vendeur', 'Oricom Internet, filiale de Bell Canada. Service de colocation non aligné au modèle vendeur.'],
                        ['Objet', 'Reprise potentielle du service de colocation sur deux sites à Québec : Serko et 400 Nolin.'],
                        ['Processus', 'Analyse préliminaire sous NDA, transparence élevée, LOI non liante possible puis décision NO-GO.'],
                      ].map(([title, text]) => (
                        <div key={title} className="rounded-lg border border-slate-100 bg-slate-50 p-4">
                          <p className="font-semibold text-slate-950">{title}</p>
                          <p className="mt-2 text-sm leading-6 text-slate-600">{text}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                </div>
              )}

              {activeTab === 'finance' && (
                <div className="space-y-6">
                  <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <h3 className="mb-4 text-lg font-bold text-slate-950">Données vendeur consolidées</h3>
                    <div className="overflow-hidden rounded-lg border border-slate-200">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-slate-100 text-xs uppercase text-slate-500">
                          <tr>
                            <th className="px-4 py-3">Année</th>
                            <th className="px-4 py-3">Revenus</th>
                            <th className="px-4 py-3">EBITDA vendeur</th>
                            <th className="px-4 py-3">Marge</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {sellerFinancials.map((row) => (
                            <tr key={row.year}>
                              <td className="px-4 py-3 font-semibold text-slate-900">{row.year}</td>
                              <td className="px-4 py-3 text-slate-700">{row.revenue}</td>
                              <td className="px-4 py-3 text-slate-700">{row.ebitda}</td>
                              <td className="px-4 py-3 text-slate-700">{row.margin}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="mt-3 text-sm text-slate-600">VAN vendeur : environ 851 k$, à traiter comme ancrage vendeur et non comme prix acceptable pour BZ.</p>
                  </section>

                  <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                      <h3 className="mb-3 flex items-center gap-2 text-lg font-bold text-slate-950"><Building2 className="h-5 w-5" /> Serko</h3>
                      <ul className="space-y-2 text-sm leading-6 text-slate-700">
                        <li>2025 / 2026 : revenus 389 k$ / 357 k$, EBITDA 224 k$ / 189 k$.</li>
                        <li>Environ 65 % de l'EBITDA total, mais site loué.</li>
                        <li>Bail expire le 31 mai 2027; renouvellement requis 3 mois avant échéance.</li>
                        <li>Cession soumise au consentement du bailleur; indexation IPC annoncée.</li>
                        <li>Capacité : 12/15 cabinets pleins, 11/16 demis, 9/20 quarts.</li>
                      </ul>
                    </section>
                    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                      <h3 className="mb-3 flex items-center gap-2 text-lg font-bold text-slate-950"><Building2 className="h-5 w-5" /> Nolin</h3>
                      <ul className="space-y-2 text-sm leading-6 text-slate-700">
                        <li>2025 / 2026 : revenus 309 k$ / 305 k$, EBITDA 113 k$ / 104 k$.</li>
                        <li>EBITDA vendeur inclut un loyer interne d'environ 83 k$/an.</li>
                        <li>Évaluation municipale : 3,18 M$; valeur uniformisée : 3,434 M$.</li>
                        <li>Taxes 2026 : environ 104 k$; entrepôt loué 5 535 $/mois.</li>
                        <li>Capacité : 29/41 cabinets pleins, 5/10 demis, 2/10 tablettes.</li>
                      </ul>
                    </section>
                  </div>

                  <section className="rounded-xl border border-red-200 bg-red-50 p-5 shadow-sm">
                    <h3 className="mb-3 text-lg font-bold text-red-950">Normalisation financière BZ</h3>
                    <p className="text-sm leading-7 text-red-900">
                      L'EBITDA vendeur n'est pas transposable. Le modèle inclut environ 66 k$/an de salaires, alors que BZ devrait prévoir au moins
                      200 k$/an pour opérer deux salles avec obligations SLA, astreinte, électricité/CVC/réseau garantis. Le transit/NNI est aussi
                      à coût nul chez Oricom, mais coûterait probablement 25 k$ à 50 k$/an à BZ. Après entretien réaliste, l'EBITDA BZ devient
                      fortement négatif à environ 90 k$ au mieux, avant churn.
                    </p>
                  </section>
                </div>
              )}

              {activeTab === 'nogo' && (
                <div className="space-y-5">
                  <div className="rounded-xl border border-red-200 bg-red-50 p-5">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="mt-1 h-6 w-6 flex-shrink-0 text-red-700" />
                      <div>
                        <h3 className="text-xl font-bold text-red-950">Décision finale : aucune LOI</h3>
                        <p className="mt-2 text-sm leading-7 text-red-900">
                          Le dossier est fermé. Aucune contre-proposition et aucune porte ouverte sur le service de colocation ou l'immeuble Nolin.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {noGoReasons.map((reason, index) => (
                      <section key={reason.title} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                        <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-bold text-red-700">Raison {index + 1}</span>
                        <h4 className="mt-3 text-lg font-bold text-slate-950">{reason.title}</h4>
                        <p className="mt-2 text-sm leading-7 text-slate-700">{reason.text}</p>
                      </section>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'files' && (
                <div className="space-y-5">
                  <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                      <div>
                        <h3 className="text-lg font-bold text-slate-950">Fichiers source uploadés</h3>
                        <p className="mt-1 text-sm text-slate-600">{files.length} fichiers · {formatBytes(totalFileSize)} dans PRISME_FILES_DB</p>
                      </div>
                      <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800">
                        <Upload className="h-4 w-4" />
                        {isUploading ? 'Upload...' : 'Ajouter fichiers'}
                        <input type="file" multiple className="hidden" onChange={handleUpload} disabled={isUploading} />
                      </label>
                    </div>
                    {filesError && (
                      <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                        {filesError}
                      </div>
                    )}
                  </section>
                  <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
                    {isFilesLoading ? (
                      <p className="p-5 text-sm text-slate-600">Chargement des fichiers...</p>
                    ) : files.length ? (
                      <div className="divide-y divide-slate-100">
                        {files.map((file) => (
                          <div key={file.id} className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
                            <div className="min-w-0">
                              <p className="truncate font-semibold text-slate-950">{file.originalName}</p>
                              <p className="mt-1 text-xs text-slate-500">{file.mimeType || 'application/octet-stream'} · {formatBytes(file.sizeBytes)}</p>
                            </div>
                            <a
                              href={getFileDownloadUrl(file.id)}
                              className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                            >
                              <Download className="h-4 w-4" />
                              Télécharger
                            </a>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="p-5 text-sm text-slate-600">Aucun fichier uploadé dans ce dossier pour le moment.</p>
                    )}
                  </section>
                </div>
              )}

              {activeTab === 'email' && (
                <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="mb-4 flex items-center justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-bold text-slate-950">Courriel de retrait</h3>
                      <p className="mt-1 text-sm text-slate-600">Bref, chaleureux, sans contre-proposition et sans porte ouverte sur ce dossier.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(withdrawalEmail)}
                      className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                    >
                      Copier
                    </button>
                  </div>
                  <pre className="whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 p-5 text-sm leading-7 text-slate-800">{withdrawalEmail}</pre>
                </section>
              )}

              {activeTab === 'avis-ca' && (
                <div className="space-y-5">
                  <section className="rounded-xl border border-blue-200 bg-blue-50 p-5 shadow-sm">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-blue-700">Avis officiel aux actionnaires / direction</p>
                        <h3 className="mt-2 text-xl font-bold text-blue-950">Courriel FD/RD — décision NO-GO documentée</h3>
                        <p className="mt-2 max-w-4xl text-sm leading-7 text-blue-900">
                          Courriel officiel envoyé le 13 juillet 2026 à François Delisle et Richard Delisle, avec Dany Bourassa en CC,
                          pour documenter la fermeture du dossier Oricom par transparence CA.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigator.clipboard?.writeText(boardNoticeEmail)}
                        className="rounded-lg bg-blue-900 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800"
                      >
                        Copier
                      </button>
                    </div>
                  </section>

                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
                    <MetricCard label="Statut" value="Envoyé" note="13 juillet 2026" tone="emerald" />
                    <MetricCard label="Destinataires" value="FD + RD" note="François et Richard Delisle" />
                    <MetricCard label="CC" value="Dany" note="dany.bourassa@bouinvest.com" />
                    <MetricCard label="Gmail ID" value="19f5c33d" note="Message 19f5c33d4a65cfaf" tone="slate" />
                  </div>

                  <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <h4 className="mb-3 text-lg font-bold text-slate-950">Résumé CA de l'avis transmis</h4>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                      {[
                        ['Décision', 'BZ ne dépose aucune LOI, ne fait aucune contre-proposition et ne garde aucune porte ouverte active sur Serko, Nolin ou l’immeuble Nolin.'],
                        ['Motif central', 'Le dossier ne cadre pas avec la capacité réelle de BZ ni avec les priorités actuelles en services Cloud et services gérés.'],
                        ['Transparence', 'François et Richard ont été informés des risques opérationnels, financiers, commerciaux et stratégiques qui justifient le NO-GO.'],
                      ].map(([title, text]) => (
                        <div key={title} className="rounded-lg border border-slate-100 bg-slate-50 p-4">
                          <p className="font-semibold text-slate-950">{title}</p>
                          <p className="mt-2 text-sm leading-6 text-slate-600">{text}</p>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <h4 className="mb-4 text-lg font-bold text-slate-950">Texte archivé du courriel</h4>
                    <pre className="whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 p-5 text-sm leading-7 text-slate-800">{boardNoticeEmail}</pre>
                  </section>
                </div>
              )}

              {activeTab === 'criteres' && (
                <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h3 className="mb-4 text-lg font-bold text-slate-950">Critères réutilisables pour futures acquisitions BZ</h3>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {reusableCriteria.map((criterion) => (
                      <div key={criterion} className="rounded-lg border border-emerald-100 bg-emerald-50 p-4 text-sm leading-6 text-emerald-950">
                        {criterion}
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
